import includes from 'lodash/includes'
import type {Moment} from 'moment-timezone'
import moment from 'moment-timezone'
import {exhaustive} from '@freckle/exhaustive'

// Close enough?
const BEGINNING_OF_TIME: Moment = moment('01-01-2000', 'MM-DD-YYYY')

export type DateOption =
  | 'today'
  | 'yesterday'
  | 'thisWeek'
  | 'lastWeek'
  | 'thisMonth'
  | 'lastMonth'
  | 'lastThreeMonths'
  | 'thisYear'
  | 'allTime'
  | 'custom'
  | 'afterToday'
  | 'last30DaysAndScheduled'

type DateOptionsT = {
  Today: DateOption
  Yesterday: DateOption
  ThisWeek: DateOption
  LastWeek: DateOption
  ThisMonth: DateOption
  LastMonth: DateOption
  LastThreeMonths: DateOption
  ThisYear: DateOption
  AllTime: DateOption
  Custom: DateOption
  AfterToday: DateOption
  Last30DaysAndScheduled: DateOption
  all: () => Array<DateOption>
  freeOptions: () => Array<DateOption>
  isFree: (dateOption: DateOption) => boolean
  parse: (str: string) => DateOption | undefined | null
}

//exported below as default
const DateOptions: DateOptionsT = {
  Today: 'today',
  Yesterday: 'yesterday',
  ThisWeek: 'thisWeek',
  LastWeek: 'lastWeek',
  ThisMonth: 'thisMonth',
  LastMonth: 'lastMonth',
  LastThreeMonths: 'lastThreeMonths',
  ThisYear: 'thisYear',
  AllTime: 'allTime',
  Custom: 'custom',
  AfterToday: 'afterToday',
  Last30DaysAndScheduled: 'last30DaysAndScheduled',
  all: function (): Array<DateOption> {
    return [
      DateOptions.Today,
      DateOptions.Yesterday,
      DateOptions.ThisWeek,
      DateOptions.LastWeek,
      DateOptions.ThisMonth,
      DateOptions.LastMonth,
      DateOptions.LastThreeMonths,
      DateOptions.ThisYear,
      DateOptions.AllTime,
      DateOptions.Custom,
    ]
  },
  freeOptions: function (): Array<DateOption> {
    return [DateOptions.Today, DateOptions.Yesterday, DateOptions.ThisWeek]
  },
  isFree: function (dateOption: DateOption): boolean {
    return includes(DateOptions.freeOptions(), dateOption)
  },
  parse: parseDateOption,
}

export default DateOptions

export function makeDateOptions(
  mDateOption?: DateOption | null | undefined,
  mFrom?: number | null,
  mTo?: number | null,
  // Default to current week
  defaultDateOption: DateOption = DateOptions.ThisWeek,
): {
  dateOption: DateOption
  from: Moment
  to: Moment
} {
  const dateOption = mDateOption ?? defaultDateOption
  const from = mFrom !== null && mFrom !== undefined ? moment(mFrom) : getMomentFrom(dateOption)
  const to = mTo !== null && mTo !== undefined ? moment(mTo) : getMomentTo(dateOption)
  return {dateOption, from, to}
}

/* Like getDateOptionForDate, but finds a DateOption that covers all the given
 * dates. Returns makeDateOptions' default if given the empty list.
 */
export function getDateOptionsForRange(dates: Array<Moment>): {
  dateOption: DateOption
  from: Moment
  to: Moment
} {
  if (dates.length === 0) {
    return makeDateOptions()
  }

  const minMoment = moment.min(dates)
  const dateOption = getDateOptionForDate(minMoment)

  return makeDateOptions(
    dateOption,
    getMomentFrom(dateOption).valueOf(),
    getMomentTo(dateOption).valueOf(),
  )
}

/* This function will return the closest DateOption for a date
 * It is used in the context of session history link of an assignment
 * When you want the link to see an assignment in session history, you can use
 * this function to find what will be the best params/filters for the session
 * history page
 */
export function getDateOptionForDate(date: Moment): DateOption {
  const [todayFrom, todayTo] = [getMomentFrom(DateOptions.Today), getMomentTo(DateOptions.Today)]
  const [yesterdayFrom, yesterdayTo] = [
    getMomentFrom(DateOptions.Yesterday),
    getMomentTo(DateOptions.Yesterday),
  ]
  const [thisWeekFrom, thisWeekTo] = [
    getMomentFrom(DateOptions.ThisWeek),
    getMomentTo(DateOptions.ThisWeek),
  ]
  const [lastWeekFrom, lastWeekTo] = [
    getMomentFrom(DateOptions.LastWeek),
    getMomentTo(DateOptions.LastWeek),
  ]
  const [thisMonthFrom, thisMonthTo] = [
    getMomentFrom(DateOptions.ThisMonth),
    getMomentTo(DateOptions.ThisMonth),
  ]
  const [lastMonthFrom, lastMonthTo] = [
    getMomentFrom(DateOptions.LastMonth),
    getMomentTo(DateOptions.LastMonth),
  ]
  const [lastThreeMonthsFrom, lastThreeMonthsTo] = [
    getMomentFrom(DateOptions.LastThreeMonths),
    getMomentTo(DateOptions.LastThreeMonths),
  ]
  const [thisYearFrom, thisYearTo] = [
    getMomentFrom(DateOptions.ThisYear),
    getMomentTo(DateOptions.ThisYear),
  ]

  if (date.isAfter(todayFrom) && date.isBefore(todayTo)) {
    return DateOptions.Today
  } else if (date.isAfter(yesterdayFrom) && date.isBefore(yesterdayTo)) {
    return DateOptions.Yesterday
  } else if (date.isAfter(thisWeekFrom) && date.isBefore(thisWeekTo)) {
    return DateOptions.ThisWeek
  } else if (date.isAfter(lastWeekFrom) && date.isBefore(lastWeekTo)) {
    return DateOptions.LastWeek
  } else if (date.isAfter(thisMonthFrom) && date.isBefore(thisMonthTo)) {
    return DateOptions.ThisMonth
  } else if (date.isAfter(lastMonthFrom) && date.isBefore(lastMonthTo)) {
    return DateOptions.LastMonth
  } else if (date.isAfter(lastThreeMonthsFrom) && date.isBefore(lastThreeMonthsTo)) {
    return DateOptions.LastThreeMonths
  } else if (date.isAfter(thisYearFrom) && date.isBefore(thisYearTo)) {
    return DateOptions.ThisYear
  } else if (date.isAfter(BEGINNING_OF_TIME) && date.isBefore(thisYearFrom)) {
    return DateOptions.Custom
  } else if (date.isAfter(todayTo)) {
    return DateOptions.ThisYear
  } else {
    return DateOptions.AllTime
  }
}

export function getMomentFrom(dateOption: DateOption): Moment {
  switch (dateOption) {
    case 'today':
      return moment().startOf('day')
    case 'yesterday':
      return moment().subtract(1, 'days').startOf('day')
    case 'thisWeek':
      return moment().startOf('isoWeek')
    case 'lastWeek':
      return moment().subtract(1, 'week').startOf('isoWeek')
    case 'thisMonth':
      return moment().startOf('month')
    case 'lastMonth':
      return moment().subtract(1, 'month').startOf('month')
    case 'lastThreeMonths':
      return moment().subtract(3, 'month').startOf('day')
    case 'thisYear':
    case 'custom': {
      const now = moment()
      const thisJuly = moment().month(6 /* july */).startOf('month')
      const lastJuly = moment().month(6 /* july */).startOf('month').subtract(1, 'year')
      return thisJuly.isAfter(now) ? lastJuly : thisJuly
    }
    case 'allTime':
      return BEGINNING_OF_TIME
    case 'afterToday':
      return moment().add(1, 'days').startOf('day')
    case 'last30DaysAndScheduled':
      return moment().subtract(30, 'days').startOf('day')
    default:
      return exhaustive(dateOption, 'DateOption')
  }
}

export function getMomentTo(dateOption: DateOption): Moment {
  switch (dateOption) {
    case 'today':
      return moment().endOf('day')
    case 'yesterday':
      return moment().subtract(1, 'days').endOf('day')
    case 'thisWeek':
      return moment().endOf('day')
    case 'lastWeek':
      return moment().subtract(1, 'week').endOf('isoWeek')
    case 'thisMonth':
      return moment().endOf('day')
    case 'lastMonth':
      return moment().subtract(1, 'month').endOf('month')
    case 'lastThreeMonths':
      return moment().endOf('day')
    case 'thisYear':
    case 'custom':
      return moment().endOf('day')
    case 'allTime':
      return moment().endOf('day')
    case 'afterToday':
      return moment().add(1, 'year')
    case 'last30DaysAndScheduled':
      return moment().add(1, 'year')
    default:
      return exhaustive(dateOption, 'DateOption')
  }
}

function formatDateFrom(dateOption: DateOption): string {
  return getMomentFrom(dateOption).format('MMM Do')
}

function formatDateTo(dateOption: DateOption): string {
  return getMomentTo(dateOption).format('MMM Do')
}

function formatDateRange(dateOption: DateOption): string {
  return `${formatDateFrom(dateOption)} - ${formatDateTo(dateOption)}`
}

// N.B. not i18n-friendly, avoid using.
export function getReadableDateOption(dateOption: DateOption): string {
  switch (dateOption) {
    case 'today':
      return 'Today'
    case 'yesterday':
      return 'Yesterday'
    case 'thisWeek':
      return 'Current Week'
    case 'lastWeek':
      return 'Last Week'
    case 'thisMonth':
      return 'Current Month'
    case 'lastMonth':
      return 'Last Month'
    case 'lastThreeMonths':
      return 'Last 3 Months'
    case 'thisYear':
      return 'This Year'
    case 'custom':
      return 'Custom time period'
    case 'allTime':
      return 'All Time'
    case 'afterToday':
      return 'Scheduled'
    case 'last30DaysAndScheduled':
      return 'Last 30 Days + Scheduled'
    default:
      return exhaustive(dateOption, 'DateOption')
  }
}

export function getDateTitle(dateOption: DateOption): string {
  const readableDateOption = getReadableDateOption(dateOption)
  const dateTitleSuffix = getDateTitleSuffix(dateOption)

  return dateTitleSuffix !== '' ? `${readableDateOption}: ${dateTitleSuffix}` : readableDateOption
}

export function getDateTitleSuffix(dateOption: DateOption): string {
  switch (dateOption) {
    case 'today':
    case 'yesterday':
      return formatDateFrom(dateOption)
    case 'thisWeek':
    case 'lastWeek':
    case 'thisMonth':
    case 'lastMonth':
    case 'lastThreeMonths':
    case 'thisYear':
      return formatDateRange(dateOption)
    case 'afterToday':
      return 'After today'
    case 'last30DaysAndScheduled':
      return `${formatDateFrom(dateOption)} - after today`
    case 'custom':
    case 'allTime':
      return ''
    default:
      return exhaustive(dateOption, 'DateOption')
  }
}

export function parseDateOption(s: string): DateOption | null {
  switch (s) {
    case 'today':
      return DateOptions.Today
    case 'yesterday':
      return DateOptions.Yesterday
    case 'thisWeek':
      return DateOptions.ThisWeek
    case 'lastWeek':
      return DateOptions.LastWeek
    case 'thisMonth':
      return DateOptions.ThisMonth
    case 'lastMonth':
      return DateOptions.LastMonth
    case 'lastThreeMonths':
      return DateOptions.LastThreeMonths
    case 'thisYear':
      return DateOptions.ThisYear
    case 'allTime':
      return DateOptions.AllTime
    case 'custom':
      return DateOptions.Custom
    case 'afterToday':
      return DateOptions.AfterToday
    case 'last30DaysAndScheduled':
      return DateOptions.Last30DaysAndScheduled
    default:
      return null
  }
}
